// The shortlist's action analytics, one call per action, for the routes that own the actions.
//
// The events, the endpoints and the wire format are `packages/ui`'s `shortlistTracking.ts`; this
// file only decides, per action, WHICH of upstream's calls it is — single or bulk, POST or GET,
// and what `value` falls back to — so the route handlers stay one line each. Every function is
// fire-and-forget: tracking never blocks or breaks the action it records.
//
// ⚠️ THE PAGE URL IS NOT PASSED. `trackingPageUrl()` reads `window.location` exactly as upstream's
// `$location.url()` does, and that string is what careero prefix-matches (CLAUDE.md constraint 1).
//
// ⚠️ `viewMode` IS WHERE THE ACTION CAME FROM, NOT A SETTING. Upstream's list passes its
// `currentViewMode` into every action service call it makes, so a list-originated event carries
// `viewMode`; the snippet (profile) passes none, the message and add-to-job services take none, and
// the snippet's Download passes `alertId` in the view-mode position — so production records
// `viewMode=undefined` there, and so does this (measured 28 Oct beside build 354). The port's list
// is always the slim layout (it has no Full mode), so the list's value is `SlimMode`.
//
// ⚠️ THE SESSION HOST IS THE SHELL'S ANSWER. `host`/`subHost` come from `GetShortlistSessionHostInfo`,
// asked once at boot (`shortlistSession.ts`); outside a frame, or until the shell answers, `host`
// goes out as upstream's own fallback (`None`).
import { ViewMode } from '@woo-ext/protocol'
import { AddAtsClientContactContext } from '@woo-ext/api'
import {
  CandidateAnalyticsEvent,
  ClientContactAnalyticsEvent,
  shortlistExtraInfo,
  shortlistExtraInfoObject,
  trackShortlistAction,
  trackShortlistBulkAction,
  trackShortlistEvent,
  type CreateInAtsTracking,
  type ExtraInfoObject,
  type ShortlistTrackingContext,
} from '@woo-ext/ui'
import { shortlistSessionHostSnapshot } from '../shortlistSession.ts'

const BASE = '/chrome-extension/extension'

/**
 * `inSearch` and `viaHunter` from the PATH, which is the state: `…shortlist.hunter.review` is
 * `/shortlist/review`, and the two hunter branches are `edit-alert`, `review` (and, for
 * candidates, `external-source-review`) under each product.
 *
 * ⚠️ THE CONTACT PROFILE OPENED BY ID IS NOT A HUNTER STATE EITHER. `client-contacts.client-contact`
 * sits beside `client-contacts.hunter`, exactly as `shortlist.candidate` sits beside
 * `shortlist.hunter`; measured on the tenant, its page view says `viaHunter=false`. This read
 * every `/client-contact` path as hunter until the page views were diffed.
 *
 * `host`/`subHost` are the shell's answer once it has arrived — see `shortlistSession.ts`.
 */
export function trackingContext(pathname: string = window.location.pathname): ShortlistTrackingContext {
  const under = (prefix: string): boolean => pathname === prefix || pathname.startsWith(`${prefix}/`)
  const session = shortlistSessionHostSnapshot()
  return {
    inSearch: under(`${BASE}/shortlist/review`),
    viaHunter:
      pathname === `${BASE}/shortlist` ||
      under(`${BASE}/shortlist/review`) ||
      under(`${BASE}/shortlist/external-source-review`) ||
      under(`${BASE}/shortlist/edit-alert`) ||
      pathname === `${BASE}/client-contact` ||
      under(`${BASE}/client-contact/review`) ||
      under(`${BASE}/client-contact/edit-alert`),
    ...(session === undefined ? {} : { host: session.host, subHost: session.subHost ?? null }),
  }
}

type Entity = { id: number } & object

/** Where a single action was clicked: the list's row strip, or the profile (snippet). */
export type ActionOrigin = 'list' | 'profile'

function context(viewMode?: ViewMode): ShortlistTrackingContext {
  return viewMode === undefined ? trackingContext() : { ...trackingContext(), viewMode }
}

const listMode = (from: ActionOrigin): ViewMode | undefined => (from === 'list' ? ViewMode.SlimMode : undefined)

/** A single action through the actions service (`trackSingleAction`, POST): value is the id. */
function single(event: string, entity: Entity, viewMode?: ViewMode, fallbackValue?: null): void {
  const extraInfo = shortlistExtraInfo(entity, null, context(viewMode))
  trackShortlistAction(event, entity.id, extraInfo, fallbackValue === null ? { fallbackValue: null } : {})
}

/** `sendToActionTracking` (GET `/track/l`) — the endpoint upstream uses for Unfavorite. */
function singleGet(event: string, entity: Entity, viewMode?: ViewMode): void {
  trackShortlistEvent(event, entity.id, shortlistExtraInfo(entity, null, context(viewMode)))
}

/**
 * The snippet's Download passes `alertId` where the service expects the view mode
 * (`extension-alert-candidate-snippet.component.ng1.ts:357` → `downloadWooProfile(candidate,
 * currentViewMode)`), and `ViewMode[69]` is undefined — recorded as the text "undefined".
 */
function snippetDownloadViewMode(alertId: number): ViewMode {
  return alertId as ViewMode
}

// ── candidates (`shortlist.candidate-actions.service.ng1.ts` and the two services beside it) ──

export const trackCandidate = {
  favor: (c: Entity, from: ActionOrigin) => single(CandidateAnalyticsEvent.Favorite, c, listMode(from)),
  unfavor: (c: Entity, from: ActionOrigin) => singleGet(CandidateAnalyticsEvent.Unfavorite, c, listMode(from)),
  hide: (c: Entity, from: ActionOrigin) => single(CandidateAnalyticsEvent.Hide, c, listMode(from)),
  unhide: (c: Entity, from: ActionOrigin) => single(CandidateAnalyticsEvent.Unhide, c, listMode(from)),
  download: (c: Entity, from: ActionOrigin, alertId: number) =>
    single(CandidateAnalyticsEvent.DownloadWooProfile, c, from === 'list' ? ViewMode.SlimMode : snippetDownloadViewMode(alertId)),
  emailDetails: (c: Entity, from: ActionOrigin) => single(CandidateAnalyticsEvent.EmailCandidateDetails, c, listMode(from)),
  assignToJob: (c: Entity, from: ActionOrigin) => single(CandidateAnalyticsEvent.AssignToJobInAts, c, listMode(from)),
  reportEnrichment: (c: Entity) => single(CandidateAnalyticsEvent.ReportEnrichment, c),
  /** The message and add-to-job services take no view mode, and pass `null` when there is no id. */
  message: (c: Entity) => single(CandidateAnalyticsEvent.Message, c, undefined, null),
  addToJob: (c: Entity) => single(CandidateAnalyticsEvent.AddToJob, c, undefined, null),
  /**
   * SINGLE_PROFILE_EXPORT's click (cee70a51e8) — `analyticsExportClickedEvent` with the id: the
   * list's `onExportCandidate` with its own view mode, or the profile service's
   * `trackExportClicked`, which passes none.
   */
  exportClicked: (c: Entity, from: ActionOrigin) => single(CandidateAnalyticsEvent.Export, c, listMode(from)),
}

/**
 * `onCandidateChecked` (component:2070) and the three multiselect handlers (:2098-2142) — the
 * list's own events, so `value` is the alert and `viewMode` is the list's.
 */
export const trackCandidateSelection = {
  checked: (alertId: number, c: Entity, checked: boolean) =>
    trackShortlistAction(
      CandidateAnalyticsEvent.SingleSelect,
      alertId,
      shortlistExtraInfo(c, { candidateId: c.id, checked: String(checked) }, context(ViewMode.SlimMode)),
    ),
  multi: (alertId: number, selectAction: 'selectAllInPage' | 'clearAllInPage' | 'selectAllInResults', checkedCount: number) =>
    trackShortlistAction(
      CandidateAnalyticsEvent.Multiselect,
      alertId,
      shortlistExtraInfo(null, { selectAction, checkedCount: String(checkedCount) }, context(ViewMode.SlimMode)),
    ),
}

/** `trackCandidateBulkAction` (extension-alert-candidates.component.ng1.ts:1755). */
function candidateBulk(singleEvent: string, bulkEvent: string, alertId: number, ids: readonly number[], rows: readonly Entity[]): void {
  trackShortlistBulkAction(singleEvent, bulkEvent, ids, {
    alertId,
    countKey: 'candidateCount',
    entityFor: (id) => rows.find((row) => row.id === id),
    context: context(ViewMode.SlimMode),
  })
}

export const trackCandidateBulk = {
  hide: (alertId: number, ids: readonly number[], rows: readonly Entity[]) =>
    candidateBulk(CandidateAnalyticsEvent.Hide, CandidateAnalyticsEvent.HideBulk, alertId, ids, rows),
  favor: (alertId: number, ids: readonly number[], rows: readonly Entity[]) =>
    candidateBulk(CandidateAnalyticsEvent.Favorite, CandidateAnalyticsEvent.FavoriteBulk, alertId, ids, rows),
  message: (alertId: number, ids: readonly number[], rows: readonly Entity[]) =>
    candidateBulk(CandidateAnalyticsEvent.Message, CandidateAnalyticsEvent.MessageBulk, alertId, ids, rows),
  addToJob: (alertId: number, ids: readonly number[], rows: readonly Entity[]) =>
    candidateBulk(CandidateAnalyticsEvent.AddToJob, CandidateAnalyticsEvent.AddToJobBulk, alertId, ids, rows),
  /** Opening the existing-list export panel (`onExportCandidatesWhenExistingListEnabled:1065`). */
  exportPanel: (alertId: number, ids: readonly number[], rows: readonly Entity[]) =>
    candidateBulk(CandidateAnalyticsEvent.Export, CandidateAnalyticsEvent.ExportBulk, alertId, ids, rows),
  /** An export that completed (`exportCandidatesDecisionData:1163`) — the pair is reversed on purpose. */
  exported: (alertId: number, ids: readonly number[], rows: readonly Entity[]) =>
    candidateBulk(CandidateAnalyticsEvent.CandidateExported, CandidateAnalyticsEvent.Export, alertId, ids, rows),
  exportToCsv: (alertId: number, ids: readonly number[], rows: readonly Entity[]) =>
    candidateBulk(CandidateAnalyticsEvent.ExportToCsv, CandidateAnalyticsEvent.ExportToCsvBulk, alertId, ids, rows),
}

// ── client contacts (`client-contact-actions.service.ng1.ts`, `client-contact-message.service`) ──

export const trackClientContact = {
  favor: (c: Entity, from: ActionOrigin) => single(ClientContactAnalyticsEvent.Favorite, c, listMode(from)),
  unfavor: (c: Entity, from: ActionOrigin) => singleGet(ClientContactAnalyticsEvent.Unfavorite, c, listMode(from)),
  hide: (c: Entity, from: ActionOrigin) => single(ClientContactAnalyticsEvent.Hide, c, listMode(from)),
  unhide: (c: Entity, from: ActionOrigin) => single(ClientContactAnalyticsEvent.Unhide, c, listMode(from)),
  message: (c: Entity) => single(ClientContactAnalyticsEvent.Message, c, undefined, null),
  /** See `trackCandidate.exportClicked` — `ClientContactExportClicked`. */
  exportClicked: (c: Entity, from: ActionOrigin) => single(ClientContactAnalyticsEvent.Export, c, listMode(from)),
}

/** `onClientContactChecked` and the multiselect handlers — the contacts twin of the above. */
export const trackClientContactSelection = {
  checked: (alertId: number, c: Entity, checked: boolean) =>
    trackShortlistAction(
      ClientContactAnalyticsEvent.SingleSelect,
      alertId,
      shortlistExtraInfo(c, { clientContactId: c.id, checked: String(checked) }, context(ViewMode.SlimMode)),
    ),
  multi: (alertId: number, selectAction: 'selectAllInPage' | 'clearAllInPage' | 'selectAllInResults', checkedCount: number) =>
    trackShortlistAction(
      ClientContactAnalyticsEvent.Multiselect,
      alertId,
      shortlistExtraInfo(null, { selectAction, checkedCount: String(checkedCount) }, context(ViewMode.SlimMode)),
    ),
}

/** `trackClientContactBulkAction` (extension-alert-client-contacts.component.ng1.ts:1583). */
function contactBulk(singleEvent: string, bulkEvent: string, alertId: number, ids: readonly number[], rows: readonly Entity[]): void {
  trackShortlistBulkAction(singleEvent, bulkEvent, ids, {
    alertId,
    countKey: 'clientContactCount',
    entityFor: (id) => rows.find((row) => row.id === id),
    context: context(ViewMode.SlimMode),
  })
}

export const trackClientContactBulk = {
  hide: (alertId: number, ids: readonly number[], rows: readonly Entity[]) =>
    contactBulk(ClientContactAnalyticsEvent.Hide, ClientContactAnalyticsEvent.HideBulk, alertId, ids, rows),
  favor: (alertId: number, ids: readonly number[], rows: readonly Entity[]) =>
    contactBulk(ClientContactAnalyticsEvent.Favorite, ClientContactAnalyticsEvent.FavoriteBulk, alertId, ids, rows),
  message: (alertId: number, ids: readonly number[], rows: readonly Entity[]) =>
    contactBulk(ClientContactAnalyticsEvent.Message, ClientContactAnalyticsEvent.MessageBulk, alertId, ids, rows),
  exportPanel: (alertId: number, ids: readonly number[], rows: readonly Entity[]) =>
    contactBulk(ClientContactAnalyticsEvent.Export, ClientContactAnalyticsEvent.ExportBulk, alertId, ids, rows),
  exported: (alertId: number, ids: readonly number[], rows: readonly Entity[]) =>
    contactBulk(ClientContactAnalyticsEvent.ClientContactExported, ClientContactAnalyticsEvent.Export, alertId, ids, rows),
}

// ── the lists' own events (`extension-alert-candidates.component.ng1.ts`, contacts twin) ──

/** `IShowFilterOption.analyticsEvent` by option name — the candidate list's (component:315-324). */
const CANDIDATE_SHOWN_EVENTS: Readonly<Record<string, string>> = {
  All: CandidateAnalyticsEvent.FilterShownAll,
  Favorites: CandidateAnalyticsEvent.FilterShownFavorites,
  Unviewed: CandidateAnalyticsEvent.FilterShownUnviewed,
  Archived: CandidateAnalyticsEvent.FilterShownArchived,
  Exported: CandidateAnalyticsEvent.FilterShownExported,
  Messaged: CandidateAnalyticsEvent.FilterShownMessaged,
  'All + Actioned': CandidateAnalyticsEvent.FilterShownActionedAndUnactioned,
}

/** The contact list's (`extension-alert-client-contacts.component.ng1.ts`, same names). */
const CONTACT_SHOWN_EVENTS: Readonly<Record<string, string>> = {
  All: ClientContactAnalyticsEvent.FilterShownAll,
  Favorites: ClientContactAnalyticsEvent.FilterShownFavorites,
  Unviewed: ClientContactAnalyticsEvent.FilterShownUnviewed,
  Archived: ClientContactAnalyticsEvent.FilterShownArchived,
  Exported: ClientContactAnalyticsEvent.FilterShownExported,
  Messaged: ClientContactAnalyticsEvent.FilterShownMessaged,
  'All + Actioned': ClientContactAnalyticsEvent.FilterShownActionedAndUnactioned,
}

/** The list component's `trackSingleAction(event, extensionAlert.id, await getExtraInfo())` — one bulk row, no entity, the list's view mode. */
function listEvent(event: string | undefined, alertId: number): void {
  if (event === undefined) return
  trackShortlistAction(event, alertId, shortlistExtraInfo(null, null, context(ViewMode.SlimMode)))
}

/** What `getCandidateDetailsByAlertId` / `getClientContactDetailsByAlertId` answer that the event reads. */
export interface ResultsLoadedCounts {
  maxResults?: number
  totalEnriched?: number
}

/**
 * `sendNewSearchMadeAction` (component:2253, contacts :2025) — after every `startNewSearch()`,
 * through `GET /track/l`. ⚠️ ITS OWN SHAPE: `value` is the ENRICHED count (as text, so a missing
 * one is the text `undefined`), the alert is the LABEL, and extraInfo is JSON, not `@@@`.
 */
function resultsLoaded(countKey: 'candidates' | 'clientContacts', alertId: number, counts: ResultsLoadedCounts): void {
  trackShortlistEvent(
    CandidateAnalyticsEvent.HunterResultsLoaded,
    String(counts.totalEnriched),
    JSON.stringify({ [countKey]: counts.maxResults, enriched: counts.totalEnriched }),
    { label: String(alertId) },
  )
}

export const trackCandidateList = {
  /** `changeShownCandidates` (:730) — tracked before anything else, even for the option already shown. */
  shownFilter: (alertId: number, optionName: string) => listEvent(CANDIDATE_SHOWN_EVENTS[optionName], alertId),
  /** `changeRelevancyDateFilter` (:634). */
  relevancyDate: (alertId: number) => listEvent(CandidateAnalyticsEvent.FilterRelevancyDate, alertId),
  resultsLoaded: (alertId: number, counts: ResultsLoadedCounts) => resultsLoaded('candidates', alertId, counts),
}

export const trackClientContactList = {
  /** `changeShownClientContacts` (:649). The contacts list has no relevancy-date filter: its event is declared and never sent. */
  shownFilter: (alertId: number, optionName: string) => listEvent(CONTACT_SHOWN_EVENTS[optionName], alertId),
  resultsLoaded: (alertId: number, counts: ResultsLoadedCounts) => resultsLoaded('clientContacts', alertId, counts),
}

// ── the snippets (`extension-alert-candidate-snippet.component.ng1.ts`, contact twin) ──

/** `scrollTop`, `scrollHeight - height()`, and the rounded percentage — `trackCandidateProfileScroll` (:280). */
export interface ProfileScrollMetrics {
  scrollTop: number
  scrollHeight: number
  scrollDepthPercentage: number
}

/** `onSocialUrlClick(socialType)` — GET, value the entity, LABEL `'' + this.alertId` (the text `undefined` without one). */
function socialUrl(event: string, entity: Entity, alertId: number | undefined, socialType: string): void {
  trackShortlistEvent(event, String(entity.id), shortlistExtraInfo(entity, { socialType }, trackingContext()), {
    label: String(alertId),
  })
}

/**
 * `trackCandidateProfileScroll` — the one shortlist event whose extraInfo is `JSON.stringify` of
 * the extra-info OBJECT rather than the `@@@` string, so its values keep their types: numbers for
 * the metrics, booleans for `inSearch`/`viaHunter`, strings for the rest. No view mode.
 */
function profileScroll(event: string, entity: Entity, alertId: number | undefined, metrics: ProfileScrollMetrics): void {
  const extraInfo: ExtraInfoObject = shortlistExtraInfoObject(entity, { ...metrics }, trackingContext())
  trackShortlistEvent(event, String(entity.id), JSON.stringify(extraInfo), { label: String(alertId) })
}

export const trackCandidateSnippet = {
  socialUrl: (c: Entity, alertId: number | undefined, socialType: string) =>
    socialUrl(CandidateAnalyticsEvent.SocialUrl, c, alertId, socialType),
  profileScroll: (c: Entity, alertId: number | undefined, metrics: ProfileScrollMetrics) =>
    profileScroll(CandidateAnalyticsEvent.ProfileScroll, c, alertId, metrics),
}

export const trackClientContactSnippet = {
  /** The template hard-codes `onSocialUrlClick('linkedin')` — the contact has one social link. */
  socialUrl: (c: Entity, alertId: number | undefined) => socialUrl(ClientContactAnalyticsEvent.SocialUrl, c, alertId, 'linkedin'),
  profileScroll: (c: Entity, alertId: number | undefined, metrics: ProfileScrollMetrics) =>
    profileScroll(ClientContactAnalyticsEvent.ProfileScroll, c, alertId, metrics),
}

/**
 * The snippet's "Create in ATS" events (`extension-alert-client-contact-snippet.component.ng1.ts:568`).
 *
 * The click and the modal's two buttons are `trackEventNicerParams` — ONE event through GET
 * `/track/l`, value the contact's id, label `'' + this.alertId` (the text `undefined` on the
 * standalone profile, which binds no alert) — and the modal's two REUSE the click's extraInfo.
 * "View in <ATS>" on the waiting modal goes through `openInAts`, i.e. `trackSingleAction`: the bulk
 * POST, value `null` (the created contact's dto has no id, so the URL's alertId or nothing), and
 * extraInfo over `{ externalId }` — the NEW contact's ATS id.
 */
export function createInAtsTracking(alertId: number | undefined): CreateInAtsTracking {
  return {
    createInAtsClicked: (contact) => {
      const extraInfo = shortlistExtraInfo(contact, null, trackingContext())
      trackShortlistEvent(ClientContactAnalyticsEvent.CreateInAts, String(contact.id), extraInfo, { label: String(alertId) })
      return extraInfo
    },
    modalSubmitClicked: (contact, context, extraInfo) => {
      trackShortlistEvent(
        context === AddAtsClientContactContext.CREATE
          ? ClientContactAnalyticsEvent.CreateInAtsModalCreate
          : ClientContactAnalyticsEvent.CreateInAtsModalUpdate,
        String(contact.id),
        extraInfo,
        { label: String(alertId) },
      )
    },
    viewCreatedInAts: (created) => {
      const extraInfo = shortlistExtraInfo(created, { externalId: created.externalId }, trackingContext())
      trackShortlistAction(ClientContactAnalyticsEvent.ViewCreatedInAts, null, extraInfo)
    },
  }
}

// ── the composer (`extension-alert-entity-messages-controller.ng1.ts:502`) ──

/**
 * `onSendButtonClick` — tracked FIRST, before the composer checks it can send at all, so a press
 * on the faded button is recorded too. `GET /track/l` with NO value (it calls `internalAnalytics`
 * directly, past the alert-id fallback), and the amounts the subclass adds
 * (`getOnSendButtonClickExtraInfo`); no view mode — the composer has none.
 */
export function trackMessageSendButton(entity: 'candidates' | 'clientContacts', checkedCount: number, validCount: number): void {
  const amounts =
    entity === 'clientContacts'
      ? { clientContactAmount: checkedCount, validAndAvailableClientContactAmount: validCount }
      : { candidateAmount: checkedCount, validAndAvailableCandidateAmount: validCount }
  trackShortlistEvent(
    entity === 'clientContacts' ? ClientContactAnalyticsEvent.MessageSendButton : CandidateAnalyticsEvent.MessageSendButton,
    undefined,
    shortlistExtraInfo(null, amounts, trackingContext()),
    { fallbackValue: undefined },
  )
}
